import { FirmRecord } from '@tassat/fred-definitions';
import { AccountRecordExtended } from '@tassat/fred-extended-definitions';
import { ActionTypes, SelectOption } from '@tassat/ui-definitions';
import { Dispatch } from 'react';

import { IErrors, IFormValues } from './types';
import { FieldValidationAction, FieldValidationState } from '../helpers/fieldValidationReducer';
import {
    textFieldCharacterWhitelist,
    validateAccountNumberFormatting,
    validateDDALengthRequirement,
    validateEmailFormatting,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable
} from '../helpers/validationHelpers';

export const validateAccountForm = async (values: IFormValues, options: {
    actionType: ActionTypes;
    initialValues: IFormValues;
    firm: FirmRecord;
    firmAccounts: AccountRecordExtended[];
    allAccounts: AccountRecordExtended[];
    ddaLengthObj: {
        ddaLengthMin: number;
        ddaLengthMax: number;
    };
    variableDDALength: boolean;
    validateDdaAccount: (
        values: IFormValues,
        firm: FirmRecord,
        timeout: string | number,
        dispatchDdaAccountAction: Dispatch<FieldValidationAction<SelectOption>>
    ) => any;
    ddaAccountState: FieldValidationState<SelectOption>;
    dispatchDdaAccountAction: Dispatch<FieldValidationAction<SelectOption>>;
    API_TIMEOUT: string | number;
}) => {
    const {
        actionType,
        initialValues,
        firm,
        firmAccounts,
        allAccounts,
        validateDdaAccount,
        ddaAccountState,
        dispatchDdaAccountAction,
        variableDDALength,
        ddaLengthObj,
        API_TIMEOUT
    } = options;
    const errors = {} as IErrors;

    const accountName = values.AccountName.trim();
    const intialAccountName = initialValues.AccountName.trim();
    const accountNameWhitelistError = textFieldCharacterWhitelist(accountName);
    const accountNameExcelError = validateNoExcelFormulaStartingCharacters(accountName);
    let accountFullNameCheck;

    if (
        actionType === ActionTypes.add ||
        (
            actionType === ActionTypes.edit &&
            (accountName !== intialAccountName)
        )
    ) {
        accountFullNameCheck = firmAccounts
            .find((account) => accountName.toLowerCase() === account.AccountName.toLowerCase());
    }

    if (!accountName) errors.AccountName = 'Required';
    else if (accountNameWhitelistError) errors.AccountName = accountNameWhitelistError;
    else if (accountNameExcelError) errors.AccountName = accountNameExcelError;
    else if (accountFullNameCheck) {
        errors.AccountName = 'BD Account Name must be unique within a firm, including closed Accounts';
    }

    if (typeof ddaAccountState.validated === 'string') {
        errors.Account = ddaAccountState.validated;

        return errors;
    } else if (
        actionType === ActionTypes.add ||
        (actionType === ActionTypes.edit && initialValues.Account !== values.Account)
    ) {
        const accountNumberFormattingError = validateAccountNumberFormatting(values.Account, undefined, { allowLeadingZeros: true });
        const accountNumberLengthError = validateDDALengthRequirement(values.Account, ddaLengthObj);
        const accountNumberError = allAccounts.find((wallet) => values.Account === wallet.Account);

        if (accountNumberFormattingError) errors.Account = accountNumberFormattingError;
        else if (accountNumberLengthError) errors.Account = accountNumberLengthError;
        else if (accountNumberError) errors.Account = 'BD Account Number must be unique';
        else if (
            ddaAccountState.validated !== true &&
            // Check isSubmitting
            (((variableDDALength && 'isSubmitting') || values.Account?.length === ddaLengthObj.ddaLengthMax)
            )) {
            try {
                const ddaError = await validateDdaAccount(values, firm, API_TIMEOUT, dispatchDdaAccountAction);
                if (ddaError) {
                    dispatchDdaAccountAction({
                        type: 'SERVER_ERROR',
                        payload: { error: ddaError }
                    });
                    errors.Account = ddaError;
                }
            } catch (error) {
                errors.Account = 'Time out, please check account number or status and try again';
            }
        } else if (ddaAccountState.validated && ddaAccountState.error) {
            errors.Account = ddaAccountState.error;
        }
    }

    const emailFormattingError = validateEmailFormatting(values.Email, true);
    const emailExcelError = validateNoExcelFormulaStartingCharacters(values.Email);
    const emailNonPrintableError = validateNoNonPrintable(values.Email);

    if (emailFormattingError) errors.Email = emailFormattingError;
    else if (emailNonPrintableError) errors.Email = emailNonPrintableError;
    else if (emailExcelError) errors.Email = emailExcelError;

    const notes = values.Notes?.trim();
    const notesWhitelistError = textFieldCharacterWhitelist(notes);
    const notesExcelError = notes?.length && validateNoExcelFormulaStartingCharacters(notes);

    if (notesWhitelistError) errors.Notes = notesWhitelistError;
    else if (notesExcelError) errors.Notes = notesExcelError;

    return errors;
};